import { z } from "zod/v4-mini";

import { ActivityIntensity, activityIntensitySchema } from "@/models/diabetes/ActivityModel.ts";
import { FoodSize, Meal, foodSizeSchema, mealSchema } from "@/models/diabetes/FoodModel.ts";
import { Schema, cz } from "@/utils/zodUtils.ts";

export type BGMConcentrationType = {
  concentration: number;
  date: string;
};

type BGMDayValue = {
  bloodSugarBefore: BGMConcentrationType[];
  bloodSugarAfter: BGMConcentrationType[];
  slowInsulin: BGMConcentrationType[];
  fastInsulin: BGMConcentrationType[];
};

type BGMNightValue = {
  bloodSugar: BGMConcentrationType[];
  slowInsulin: BGMConcentrationType[];
};

export type FoodComment = {
  comments?: string;
  meal?: Meal;
  size?: FoodSize;
  carbs?: number;
};

export type ActivityCommentType = {
  date: string;
  duration: number;
  intensity: ActivityIntensity;
};

export type EventCommentType = {
  date: string;
  category: "severeHypoglycemia" | "perceivedHypoglycemia" | "perceivedHyperglycemia" | "techAlert" | "other";
  message: string;
};

export type CommentType = {
  food: FoodComment[];
  activity: ActivityCommentType[];
  event: EventCommentType[];
};

export type BGMData = {
  date: string;
  breakfast: BGMDayValue;
  lunch: BGMDayValue;
  dinner: BGMDayValue;
  night: BGMNightValue;
  comments: CommentType;
};

const bgmConcentrationSchema: Schema<BGMConcentrationType> = z.object({
  concentration: z.number(),
  date: z.string(),
});
const bgmDayValueSchema: Schema<BGMDayValue> = z.object({
  bloodSugarBefore: cz.forcedArray(bgmConcentrationSchema),
  bloodSugarAfter: cz.forcedArray(bgmConcentrationSchema),
  slowInsulin: cz.forcedArray(bgmConcentrationSchema),
  fastInsulin: cz.forcedArray(bgmConcentrationSchema),
});

export const bgmDataSchema: Schema<BGMData> = z.object({
  date: z.string(),
  breakfast: bgmDayValueSchema,
  lunch: bgmDayValueSchema,
  dinner: bgmDayValueSchema,
  night: z.object({
    bloodSugar: cz.forcedArray(bgmConcentrationSchema),
    slowInsulin: cz.forcedArray(bgmConcentrationSchema),
  }),
  comments: z.object({
    food: cz.forcedArray(
      z.object({
        meal: cz.optional(mealSchema),
        size: cz.optional(foodSizeSchema),
        carbs: cz.optional(z.number()),
        comments: cz.optional(z.string()),
      }),
    ),
    activity: cz.forcedArray(
      z.object({
        date: z.string(),
        intensity: activityIntensitySchema,
        duration: z.number(),
      }),
    ),
    event: cz.forcedArray(
      z.object({
        date: z.string(),
        category: z.literal([
          "severeHypoglycemia",
          "perceivedHypoglycemia",
          "perceivedHyperglycemia",
          "techAlert",
          "other",
        ]),
        message: z.string(),
      }),
    ),
  }),
});
